import { getAnnualGHGReductionLabel, getAnnualGHGReductionLabelFromParens } from "./ghgLabelHelpers";
import React from "react";
import { Grid, Typography, TextField, Stack, Box} from "@mui/material";
import SingleDropDown from "components/Dropdowns/SingleDropDown/SingleDropDown";
import CreatableSingleDropDown from "components/Dropdowns/CreatableSingleDropDown/CreatableSingleDropDown";
import TitleDefinition from "components/TitleDefinition";
import { CreateUpdateReductionMeasuresDesignProps } from "./data/CreateUpdateReductionMeasuresDesignProps";
import { getEnergyUnits } from "./data/utils";
import {
    ReductionMeasureLists,
    TitleDefinitionConstants,
} from "constants/constant";

export default function MeasureReduction(
  props: Readonly<CreateUpdateReductionMeasuresDesignProps>
) {
  const ghgUnitOptionsByCarrier = props.ghgUnitOptionsByCarrier ?? {};

  const getEffectiveCarrierType = (carrier: {
    ghgType?: string;
    customGHGType?: string;
    CustomGHGType?: string;
    customGHGEmissionFactor?: { ghgType?: string };
    CustomGHGEmissionFactor?: { GHGType?: string };
  }): string => {
    const carrierType = carrier.ghgType?.trim();
    const resolvedCustomType = [
      carrier.customGHGType?.trim(),
      carrier.CustomGHGType?.trim(),
      carrier.customGHGEmissionFactor?.ghgType?.trim(),
      carrier.CustomGHGEmissionFactor?.GHGType?.trim(),
    ].find((value): value is string => value != null && value.length > 0);

    return carrierType && carrierType !== "Custom" ? carrierType : (resolvedCustomType ?? "");
  };

  const getCarrierUnitSource = (
    carrier: {
      ghgType?: string;
      customGHGType?: string;
      CustomGHGType?: string;
      customGHGEmissionFactor?: { ghgType?: string };
      CustomGHGEmissionFactor?: { GHGType?: string };
    },
    index: number
  ): string[] => {
    const effectiveType = getEffectiveCarrierType(carrier);
    const hasPersistedCustomType =
      hasValue(carrier.customGHGType) ||
      hasValue(carrier.CustomGHGType) ||
      hasValue(carrier.customGHGEmissionFactor?.ghgType) ||
      hasValue(carrier.CustomGHGEmissionFactor?.GHGType);
    const isKnownPredefinedType =
      Boolean(effectiveType && predefinedGHGTypes.has(effectiveType)) &&
      !hasPersistedCustomType;

    const allPredefinedUnits = props.ghgEmissionFactorList.map((g) => g.ghgUnit);
    const typeSpecificPredefinedUnits = props.ghgEmissionFactorList
      .filter((g) => g.ghgType === effectiveType)
      .map((g) => g.ghgUnit);

    const hasCarrierOptions = Object.hasOwn(ghgUnitOptionsByCarrier, index);
    const carrierOptions = ghgUnitOptionsByCarrier[index] ?? [];
    if (hasCarrierOptions) {
      if (!isKnownPredefinedType) {
        return Array.from(new Set([...allPredefinedUnits, ...carrierOptions]));
      }

      return Array.from(new Set([...typeSpecificPredefinedUnits, ...carrierOptions]));
    }

    if (!isKnownPredefinedType) {
      return allPredefinedUnits;
    }

    return typeSpecificPredefinedUnits;
  };

  const predefinedGHGTypes = new Set(
    props.ghgEmissionFactorList
      .map((g) => g.ghgType)
      .filter((value): value is string => Boolean(value && value.length > 0) && value !== "Custom")
  );

  const hasValue = (value: unknown): boolean => {
    if (value === undefined || value === null) {
      return false;
    }
    if (typeof value === "string") {
      return value.trim().length > 0;
    }
    if (typeof value === "number") {
      return !Number.isNaN(value);
    }
    if (typeof value === "boolean") {
      return true;
    }
    return false;
  };

  const getNormalizedEnergyUnits = (energyType: string): string[] =>
    Array.from(
      new Set(
        getEnergyUnits(props.energyTypeList, energyType)
          .map((unit) => unit.trim())
          .filter((unit) => unit.length > 0)
      )
    );

  const getSelectedEnergyUnit = (energyType: string, currentUnit?: string): string => {
    const normalizedCurrentUnit = currentUnit?.trim() ?? "";
    if (!normalizedCurrentUnit) {
      return "";
    }

    return getNormalizedEnergyUnits(energyType).includes(normalizedCurrentUnit)
      ? normalizedCurrentUnit
      : "";
  };

  return (
    <>
      <Typography
          variant="h6"
          data-testid={"measureReduction"}
          sx={{ fontWeight: 600, fontSize: 12, mb: 2 }}
        >
          Measure Reduction
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sx={{ mt: 1 }}>
            <Stack direction={"row"}>
              <Typography
                variant="body1"
                data-testid={"ghgReductionLabel"}
                sx={{ mb: 2, fontWeight: 600, fontSize: 12 }}
              >
                Energy or GHG Reduction
                <Typography component="span" sx={{ color: "red", fontWeight: 600, fontSize: 12 }}>
                  *
                </Typography>
              </Typography>
              <TitleDefinition
                content={
                  TitleDefinitionConstants.ReductionMeasure.EnergyGHGReduction
                }
                placement="auto"
              />
            </Stack>
            <SingleDropDown
              required
              onChange={props.fieldChangeHandler}
              list={ReductionMeasureLists.EmissionTypeList}
              value={props.draftReductionMeasure.ghgReduction}
              name={"ghgReduction"}
              sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
            />
          </Grid>
          <Grid item xs={6} sx={{ mt: 1 }}>
            <Stack direction={"row"}>
              <Typography
                variant="body1"
                data-testid={"noOfCarrierLabel"}
                sx={{ mb: 2, fontWeight: 600, fontSize: 12 }}
              >
                Number of Carriers
                <Typography component="span" sx={{ color: "red", fontWeight: 600, fontSize: 12 }}>
                  *
                </Typography>
              </Typography>
              <TitleDefinition
                content={
                  TitleDefinitionConstants.ReductionMeasure.NumberofCarriers
                }
                placement="auto"
              />
            </Stack>
            <SingleDropDown
                required
                onChange={props.fieldChangeHandler}
                list={ReductionMeasureLists.NoOfCarriers}
                value={props.draftReductionMeasure.noOfCarrier?.toString()}
                name={"noOfCarrier"}
                disabled={!props.draftReductionMeasure.ghgReduction}
                sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
              />
              {!props.draftReductionMeasure.ghgReduction && (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", mt: 0.5, display: "block", fontSize: 11 }}
                >
                  Please select an Energy or GHG Reduction type to enable this field.
                </Typography>
              )}
          </Grid>
          {props.draftReductionMeasure.ghgCarriers?.map((Carrier, i) => {
            const hasPersistedCustomGhgType =
              hasValue(Carrier.customGHGType) ||
              hasValue(Carrier.CustomGHGType) ||
              hasValue(Carrier.customGHGEmissionFactor?.ghgType) ||
              hasValue(Carrier.CustomGHGEmissionFactor?.GHGType);

            const isCustomGhgType = Boolean(
              hasPersistedCustomGhgType ||
                (Carrier.ghgType && !predefinedGHGTypes.has(Carrier.ghgType))
            );

            const hasPersistedCustomGhgUnit =
              hasValue(Carrier.customGHGUnit) ||
              hasValue(Carrier.CustomGHGUnit) ||
              hasValue(Carrier.customGHGEmissionFactor?.ghgUnit) ||
              hasValue(Carrier.CustomGHGEmissionFactor?.GHGUnit);

            const predefinedGHGUnits = new Set(
              props.ghgEmissionFactorList
                .filter((g) => g.ghgType === Carrier.ghgType)
                .map((g) => g.ghgUnit)
                .filter(
                  (value): value is string =>
                    Boolean(value && value.length > 0) && value !== "Custom"
                )
            );
            const isCustomGhgUnit = Boolean(
              hasPersistedCustomGhgUnit ||
                (Carrier.ghgUnit && !predefinedGHGUnits.has(Carrier.ghgUnit))
            );

            const customCO2eEFValue =
              Carrier.customCO2e_EF ??
              Carrier.CustomCO2e_EF ??
              Carrier.customGHGEmissionFactor?.cO2e_EF ??
              Carrier.CustomGHGEmissionFactor?.CO2e_EF ??
              "";

            const showCustomCO2eEF =
              isCustomGhgType ||
              hasValue(customCO2eEFValue);

            const annualGHGReductionLabel =
              isCustomGhgType || isCustomGhgUnit
                ? getAnnualGHGReductionLabel(Carrier, {
                    isCustomGhgType,
                    isCustomGhgUnit,
                  })
                : getAnnualGHGReductionLabelFromParens(Carrier, {
                    isCustomGhgType,
                    isCustomGhgUnit,
                  });

            return (
              <React.Fragment key={Carrier.id}>
                <Grid item xs={11}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 18,
                      fontWeight: "600",
                      color: "#718096",
                    }}
                    data-testid={"GHGCarrier" + (i + 1)}
                  >
                    GHG Carrier {i + 1}
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ mt: 1 }}>
                  <Stack direction={"row"}>
                    <Typography
                      variant="body1"
                      data-testid={"ghgScopeLabel"}
                      sx={{ mb: 2, fontWeight: 600, fontSize: 12 }}
                    >
                      GHG Scope
                      <Typography component="span" sx={{ color: "red" }}>
                        *
                      </Typography>
                    </Typography>
                    <TitleDefinition
                      content={
                        TitleDefinitionConstants.ReductionMeasure.GHGScope
                      }
                      placement="auto"
                    />
                  </Stack>
                  <SingleDropDown
                    required
                    onChange={(e) => {
                      props.ghgCarrierfieldChangeHandler(e, i);
                    }}
                    list={props.gHGScopeList.map((g) => g.value)}
                    value={Carrier.ghgScope}
                    name={"ghgScope"}
                    sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
                  />
                </Grid>
                <Grid item xs={6} sx={{ mt: 1 }}>
                  <Stack direction={"row"}>
                    <Typography
                      variant="body1"
                      data-testid={"ghgCategoryLabel"}
                      sx={{ mb: 2, fontWeight: 600, fontSize: 12 }}
                    >
                      GHG Category
                      <Typography component="span" sx={{ color: "red" }}>
                        *
                      </Typography>
                    </Typography>
                    <TitleDefinition
                      content={
                        TitleDefinitionConstants.ReductionMeasure.GHGCategory
                      }
                      placement="auto"
                    />
                  </Stack>
                  <SingleDropDown
                    disabled={!Carrier.ghgScope}
                    required
                    onChange={(e) => {
                      props.ghgCarrierfieldChangeHandler(e, i);
                    }}
                    list={props.gHGCategoryList
                      .filter(
                        (gc) => gc.type === Carrier.ghgScope?.replace(" ", "")
                      )
                      .map((g) => g.value)}
                    value={Carrier.ghgCategory}
                    name={"ghgCategory"}
                    sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
                  />
                </Grid>
                {props.draftReductionMeasure.ghgReduction ===
                ReductionMeasureLists.EmissionTypeList[0] ? (
                  <React.Fragment>
                    <Grid item xs={6} sx={{ mt: 1 }}>
                      <Stack direction={"row"}>
                        <Typography
                          variant="body1"
                          data-testid={"energyCategoryLabel" + (i + 1)}
                          sx={{ mb: 2, fontWeight: 600, fontSize: 12 }}
                        >
                          Energy Category
                          <Typography component="span" sx={{ color: "red" }}>
                            *
                          </Typography>
                        </Typography>
                        <TitleDefinition
                          content={
                            TitleDefinitionConstants.ReductionMeasure
                              .EnergyCategory
                          }
                          placement="auto"
                        />
                      </Stack>
                      <SingleDropDown
                        required
                        onChange={(e) => {
                          props.ghgCarrierfieldChangeHandler(e, i);
                        }}
                        list={props.energyCategoryList.map((g) => g.value)}
                        value={Carrier.energyCategory}
                        name={"energyCategory"}
                        sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
                      />
                    </Grid>
                    <Grid item xs={6} sx={{ mt: 1 }}>
                      <Stack direction={"row"}>
                        <Typography
                          variant="body1"
                          data-testid={"energyTypeLabel" + (i + 1)}
                          sx={{ mb: 2, fontWeight: 600, fontSize: 12 }}
                        >
                          Energy Type
                          <Typography component="span" sx={{ color: "red" }}>
                            *
                          </Typography>
                        </Typography>
                        {Carrier.energyCategory === "Electricity" && (
                          <TitleDefinition
                            content={
                              TitleDefinitionConstants.ReductionMeasure
                                .EnergyType
                            }
                            placement="auto"
                          />
                        )}
                      </Stack>

                      <SingleDropDown
                        required
                        onChange={(e) => {
                          props.ghgCarrierfieldChangeHandler(e, i);
                        }}
                        list={props.energyTypeList
                          .filter((e) => e.type === Carrier.energyCategory)
                          .map((g) => g.value)}
                        value={
                          Carrier.energyType &&
                          props.energyTypeList
                            .filter((e) => e.type === Carrier.energyCategory)
                            .map((g) => g.value)
                            .includes(Carrier.energyType)
                            ? Carrier.energyType
                            : ""
                        }
                        name={"energyType"}
                        sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
                      />
                    </Grid>
                    {Carrier.ghgScope === "Scope 2" &&
                      (Carrier.ghgCategory === "Electricity" ||
                        Carrier.ghgCategory === "Steam") && (
                        <>
                          <Grid item xs={6} sx={{ mt: 1 }}>
                            <Stack direction={"row"}>
                              <Typography
                                variant="body1"
                                data-testid={"countryLabel" + (i + 1)}
                                sx={{ mb: 2, fontWeight: 600, fontSize: 12 }}
                              >
                                Country
                                <Typography
                                  component="span"
                                  sx={{ color: "red" }}
                                >
                                  *
                                </Typography>
                              </Typography>
                            </Stack>
                            <SingleDropDown
                              required
                              onChange={(e) => {
                                props.ghgCarrierfieldChangeHandler(e, i);
                              }}
                              list={Array.from(
                                new Set(props.countryList.map((c) => c.country))
                              )}
                              value={Carrier.country}
                              name="country"
                              sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
                            />
                          </Grid>
                          <Grid item xs={6} sx={{ mt: 1 }}>
                            <Typography
                              variant="body1"
                              data-testid={"regionLabel" + (i + 1)}
                              sx={{ mb: 2, fontWeight: 600, fontSize: 12 }}
                            >
                              Region
                              <Typography
                                component="span"
                                sx={{ color: "red" }}
                              >
                                *
                              </Typography>
                            </Typography>
                            <SingleDropDown
                              required
                              onChange={(e) => {
                                props.ghgCarrierfieldChangeHandler(e, i);
                              }}
                              list={props.countryList
                                .filter((gc) => gc.country === Carrier.country)
                                .map((g) => g.region)}
                              value={Carrier.region}
                              name={"region"}
                              sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
                            />
                          </Grid>
                        </>
                      )}

                    <Grid item xs={6} sx={{ mt: 1 }}>
                      <Stack direction={"row"}>
                        <Typography
                          variant="body1"
                          data-testid={"EnergyUnits" + (i + 1)}
                          sx={{ mb: 2, fontWeight: 600, fontSize: 12 }}
                        >
                          Energy Units
                        </Typography>
                        <TitleDefinition
                          content={
                            TitleDefinitionConstants.ReductionMeasure
                              .EnergyUnits
                          }
                          placement="auto"
                        />
                      </Stack>
                      <SingleDropDown
                        disabled={
                          !(Carrier.energyType && Carrier.energyType.length > 0)
                        }
                        required
                        onChange={(e) => {
                          props.ghgCarrierfieldChangeHandler(e, i);
                        }}
                        list={getNormalizedEnergyUnits(Carrier.energyType ?? "")}
                        value={getSelectedEnergyUnit(
                          Carrier.energyType ?? "",
                          Carrier.energyUnits
                        )}
                        name={"energyUnits"}
                        sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
                      />
                    </Grid>

                    <Grid item xs={6} sx={{ mt: 1 }}>
                      <Stack direction={"row"}>
                        <Typography
                          variant="body1"
                          data-testid={"AnnualEnergyReductionLabel" + (i + 1)}
                          sx={{ fontWeight: 600, fontSize: 12 }}
                        >
                          Annual Energy Reduction
                        </Typography>
                        <TitleDefinition
                          content={
                            TitleDefinitionConstants.ReductionMeasure
                              .AnnualEnergyReduction
                          }
                          placement="auto"
                        />
                      </Stack>

                      <Box sx={{ height: 12 }} />
                      <TextField
                        id={"AnnualEnergyReduction" + (i + 1)}
                        inputProps={{
                          maxLength: 12,
                          "data-testid": "AnnualEnergyReduction" + (i + 1),
                          style: { fontSize: 12, padding: '4px 6px', height: 30 }
                        }}
                        fullWidth
                        margin="none"
                        size="small"
                        name={"annualReduction"}
                        value={Carrier.annualReduction ?? ""}
                        onChange={(e) => {
                          props.ghgCarrierfieldChangeHandler(e, i);
                        }}
                        sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '7px 6px 4px 6px', boxSizing: 'border-box' } }}
                      />
                    </Grid>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <Grid item xs={6} sx={{ mt: 1 }}>
                      <Stack direction={"row"}>
                        <Typography
                          variant="body1"
                          data-testid={"ghgTypeLabel" + (i + 1)}
                          sx={{ mb: 2, fontWeight: 600, fontSize: 12 }}
                        >
                          GHG Type
                          <Typography component="span" sx={{ color: "red" }}>
                            *
                          </Typography>
                        </Typography>
                        <TitleDefinition
                          content={TitleDefinitionConstants.ReductionMeasure.GHGType}
                          placement="auto"
                        />
                      </Stack>
                      <CreatableSingleDropDown
                        required
                        onChange={(e) => {
                          props.ghgCarrierfieldChangeHandler(e, i);
                        }}
                        list={Array.from(
                          new Set(
                            [...props.ghgEmissionFactorList.map((g) => g.ghgType), Carrier.ghgType]
                              .filter(
                                (value): value is string =>
                                  Boolean(value && value.length > 0) && value !== "Custom"
                              )
                          )
                        )}
                        placeholder={"Select or type a GHG Type"}
                        value={Carrier.ghgType ?? ""}
                        name={"ghgType"}
                        sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-root': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
                      />
                    </Grid>
                    <Grid item xs={6} sx={{ mt: 1 }}>
                      <Stack direction={"row"}>
                        <Typography
                          variant="body1"
                          data-testid={"ghgUnitLabel" + (i + 1)}
                          sx={{ mb: 2, fontWeight: 600, fontSize: 12 }}
                        >
                          GHG Unit
                          <Typography component="span" sx={{ color: "red" }}>
                            *
                          </Typography>
                        </Typography>
                        <TitleDefinition
                          content={TitleDefinitionConstants.ReductionMeasure.GHGUnit}
                          placement="auto"
                        />
                      </Stack>
                      <CreatableSingleDropDown
                        disabled={!getEffectiveCarrierType(Carrier)}
                        required
                        onChange={(e) => {
                          props.ghgCarrierfieldChangeHandler(e, i);
                        }}
                        list={Array.from(
                          new Set(
                            [
                              ...getCarrierUnitSource(Carrier, i),
                              Carrier.ghgUnit,
                            ].filter(
                              (value): value is string =>
                                Boolean(value && value.length > 0) && value !== "Custom"
                            )
                          )
                        )}
                        placeholder={"Select or type a GHG Unit"}
                        value={Carrier.ghgUnit ?? ""}
                        name={"ghgUnit"}
                        sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-root': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
                      />
                    </Grid>
                    {showCustomCO2eEF ? (
                      <Grid item xs={12} sx={{ mt: 1 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Box sx={{ minHeight: 24, mb: 2, display: "flex", alignItems: "center" }}>
                              <Typography
                                variant="body1"
                                data-testid={"customCO2eEFLabel" + (i + 1)}
                                sx={{ fontWeight: 600, fontSize: 12 }}
                              >
                                Custom CO2e EF
                              </Typography>
                            </Box>
                            <TextField
                              id={"customCO2eEF" + (i + 1)}
                              inputProps={{
                                maxLength: 18,
                                step: "0.0001",
                                "data-testid": "customCO2eEF" + (i + 1),
                                style: { fontSize: 12, padding: "4px 6px", height: 30 },
                              }}
                              type="number"
                              fullWidth
                              margin="none"
                              size="small"
                              name={"customCO2e_EF"}
                              value={customCO2eEFValue}
                              onChange={(e) => {
                                props.ghgCarrierfieldChangeHandler(e, i);
                              }}
                              sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: "border-box", ".MuiInputBase-input": { height: 30, padding: "4px 6px", boxSizing: "border-box" } }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Box sx={{ minHeight: 24, mb: 2, display: "flex", alignItems: "center" }}>
                              <Stack direction={"row"} alignItems="center">
                                <Typography
                                  variant="body1"
                                  data-testid={"AnnualGHGReductionLabel" + (i + 1)}
                                  sx={{ fontWeight: 600, fontSize: 12 }}
                                >
                                  {annualGHGReductionLabel}
                                </Typography>
                                <TitleDefinition
                                  content={
                                    TitleDefinitionConstants.ReductionMeasure
                                      .AnnualGHGReduction
                                  }
                                  placement="auto"
                                />
                              </Stack>
                            </Box>

                            <TextField
                              id={"AnnualGHGReduction" + (i + 1)}
                              inputProps={{
                                maxLength: 12,
                                "data-testid": "AnnualGHGReduction" + (i + 1),
                                step: "0.01",
                                style: { fontSize: 12, padding: '4px 6px', height: 30 }
                              }}
                              type="number"
                              fullWidth
                              margin="none"
                              size="small"
                              name={"annualReduction"}
                              value={Carrier.annualReduction}
                              onChange={(e) => {
                                props.ghgCarrierfieldChangeHandler(e, i);
                              }}
                              sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid item xs={6} sx={{ mt: 1 }}>
                        <Stack direction={"row"}>
                          <Typography
                            variant="body1"
                            data-testid={"AnnualGHGReductionLabel" + (i + 1)}
                            sx={{ fontWeight: 600, fontSize: 12 }}
                          >
                            {annualGHGReductionLabel}
                          </Typography>
                          <TitleDefinition
                            content={
                              TitleDefinitionConstants.ReductionMeasure
                                .AnnualGHGReduction
                            }
                            placement="auto"
                          />
                        </Stack>

                        <TextField
                          id={"AnnualGHGReduction" + (i + 1)}
                          inputProps={{
                            maxLength: 12,
                            "data-testid": "AnnualGHGReduction" + (i + 1),
                            step: "0.01",
                            style: { fontSize: 12, padding: '4px 6px', height: 30 }
                          }}
                          type="number"
                          fullWidth
                          margin="none"
                          size="small"
                          name={"annualReduction"}
                          value={Carrier.annualReduction}
                          onChange={(e) => {
                            props.ghgCarrierfieldChangeHandler(e, i);
                          }}
                          sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
                        />
                      </Grid>
                    )}
                  </React.Fragment>
                )}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant="body1" data-testid={"Notes"} sx={{ fontWeight: 600, fontSize: 12 }}>
                    Notes
                  </Typography>
                  <TextField
                    id={"Notes" + (i + 1)}
                    multiline
                    rows={2}
                    inputProps={{
                      flexWrap: "wrap",
                      maxLength: 300,
                      "data-testid": "Notes" + (i + 1),
                      style: { fontSize: 12, padding: '4px 6px', height: 30 }
                    }}
                    fullWidth
                    margin="none"
                    size="small"
                    name={"notes"}
                    value={Carrier.notes}
                    onChange={(e) => {
                      props.ghgCarrierfieldChangeHandler(e, i);
                    }}
                    sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', mb: 6, '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
                  />
                </Grid>
                {i < props.draftReductionMeasure.ghgCarriers.length - 1 && (
                  <Grid item xs={12}>
                    <Box sx={{ my: 2 }}>
                      <hr style={{ border: 0, borderTop: '1px solid #e0e0e0', margin: '12px 0 0 0' }} />
                    </Box>
                  </Grid>
                )}
              </React.Fragment>
            );
          })}
        </Grid>
    </>
  );
}